import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { serviceFaqs } from "@/data/serviceFaqs";
import img from "@/assets/lighting.jpg";
import { serviceGraph, abs } from "@/lib/serviceSchema";


export const Route = createFileRoute("/services/lighting")({
  head: () => ({
    meta: [
      { title: "Smart Lighting Design & Install | Apex AV Denver" },
      { name: "description", content: "Architectural smart lighting design for Denver homes — tunable white & circadian lighting with scenes. Schedule a free lighting consultation." },
      { name: "keywords", content: "smart lighting Denver, lighting control Denver, architectural lighting Denver, lighting design Denver, keypad lighting control Denver, tunable white lighting Denver, circadian lighting Denver, dimmer installation Denver, whole-home lighting control Denver, luxury lighting automation Denver" },
      { property: "og:title", content: "Smart Lighting Design — Apex AV Denver" },
      { property: "og:description", content: "Architectural lighting design that transforms your Denver home. Free lighting consultation." },
      { property: "og:image", content: abs(img) },
      { name: "twitter:image", content: abs(img) },
      { property: "og:url", content: "https://apexav.com/services/lighting" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Smart Lighting Design — Apex AV Denver" },
      { name: "twitter:description", content: "Architectural lighting design that transforms your Denver home. Free lighting consultation." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/services/lighting" }, { rel: "preload", as: "image", href: img, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "lighting",
            name: "Smart Lighting Control",
            description: "Architectural lighting design with tunable white and circadian scenes in Denver.",
            pageTitle: "Smart Lighting Design & Install | Apex AV Denver",
            pageDescription: "Architectural smart lighting design for Denver homes — tunable white & circadian lighting with scenes. Schedule a free lighting consultation.",
            image: img,
            faqs: serviceFaqs["lighting"],
            serviceType: "Smart Lighting Control",
            offerCatalog: [
              "Certified installer of leading lighting control platforms",
              "Tunable white and full-color circadian lighting",
              "Custom keypad engraving and scene programming",
              "Retrofit and new-construction wiring",
              "Dim-to-warm LED specification and testing",
              "Integrated with shading, climate, audio, and security",
            ],
            breadcrumbName: "Smart Lighting",
            extraNodes: [
              {
                "@type": "VideoObject",
                name: "Smart Lighting Design & Install — Apex AV Denver",
                description: "Architectural smart lighting design for Denver luxury homes. Tunable white and circadian lighting that adapts to time of day and scene.",
                thumbnailUrl: ["https://apexav.com/videos/lighting.jpg"],
                uploadDate: "2026-06-01T00:00:00-06:00",
                contentUrl: "https://apexav.com/videos/walk-in-lights-on.mp4",
                embedUrl: "https://apexav.com/services/lighting",
              },
            ],
          })
        ),
      },
    ],
  }),
  component: () => (
    <ServiceDetail
      faqs={serviceFaqs["lighting"]}
      topics={["lighting", "shading", "smart-home"]}
      eyebrow="Lighting design"
      title="Architectural lighting, on cue."
      intro={<>Architectural <strong>smart lighting in Denver</strong> with tunable white, full-color <strong>circadian lighting</strong>, keypad control, and whole-home scenes. From single-room dimming to <strong>lighting control</strong>, we design light around how you live.</>}
      image={img}
      imageAlt="Architectural smart lighting control installation in a Castle Rock Denver luxury home"
      video="/videos/walk-in-lights-on.mp4"
      bullets={[
        "Certified installer of leading lighting control platforms",
        "Tunable white and full-color circadian lighting",
        "Custom keypad engraving and scene programming",
        "Retrofit and new-construction wiring",
        "Dim-to-warm LED specification and testing",
        "Integrated with shading, climate, audio, and security",
      ]}
      features={[
        { title: "Scene programming", desc: "'Movie', 'Dinner', 'Goodnight' — one tap, every fixture in concert." },
        { title: "Circadian health", desc: "Cool, energizing light by day. Warm, restful tones by evening." },
        { title: "Designer keypads", desc: "Custom-engraved keypads in dozens of finishes to match any interior." },
      ]}
    />
  ),
});
